import { useJsApiLoader, GoogleMap } from "@react-google-maps/api";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "../utils/googleMaps";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
  BellIcon,
  DocumentDuplicateIcon,
  EllipsisHorizontalIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  TrashIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { Checkbox } from "../components/Checkbox";
import { PrimaryButtonSmall } from "../components/Button";
import { showToast } from "../utils/showToast";
import { getDateWarning, getDatePushWarning } from "./utils/formUtils";
import { toDateSafe, formatEventDate } from "./formSectionHelpers";
import { eventDetailsUrl } from "../utils/links";

export const PlacesDropdown = ({ suggestions, onSelect }) => {
  if (!suggestions.length) return null;

  return (
    <ul className="absolute z-50 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg mt-1 max-h-60 overflow-auto">
      {suggestions.map((s, i) => {
        const pred = s.placePrediction;
        const mainText =
          pred?.structuredFormat?.mainText?.text || pred?.text?.text || "";
        const secondaryText = pred?.structuredFormat?.secondaryText?.text || "";

        return (
          <li
            key={i}
            tabIndex={-1}
            onMouseDown={() => onSelect(s)}
            className="px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer focus:bg-gray-50 dark:focus:bg-gray-700 focus:outline-none"
          >
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {mainText}
            </p>
            {secondaryText && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{secondaryText}</p>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export const VenueMapPreview = ({ venueName, address }) => {
  const query = encodeURIComponent(
    [venueName, address].filter(Boolean).join(", ")
  );
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${query}`;
  const embedUrl = `https://maps.google.com/maps?q=${query}&z=13&output=embed`;

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
      <div className="relative h-64 w-full bg-gray-100 dark:bg-gray-800">
        <iframe
          title="Venue location preview"
          src={embedUrl}
          width="100%"
          height="100%"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="absolute inset-0 h-full w-full border-0"
        />
      </div>
      <div className="flex items-start gap-3 px-4 py-3">
        <svg
          className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary"
          fill="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 010-5 2.5 2.5 0 010 5z" />
        </svg>
        <div className="min-w-0 flex-1">
          {venueName && (
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {venueName}
            </p>
          )}
          {address && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{address}</p>
          )}
        </div>
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-shrink-0 text-xs font-medium text-primary hover:underline"
        >
          Open in Maps
        </a>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// HostPinInput — "Group spot" control. Opens a draggable-pin map picker and,
// once set, shows a small read-only preview of events.hostPin + its label.
// ---------------------------------------------------------------------------
export const HostPinInput = ({ hostPin, onOpenPicker, onClear }) => {
  const hasPin =
    hostPin &&
    typeof hostPin.lat === "number" &&
    typeof hostPin.lng === "number";

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
          Group spot
        </label>
        {hasPin && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400"
          >
            Clear pin
          </button>
        )}
      </div>
      <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
        Drop a pin where the group is sitting so attendees can find you.
      </p>

      {hasPin ? (
        <div className="mt-2 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
          <div className="relative h-48 w-full bg-gray-100 dark:bg-gray-800">
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={{ lat: hostPin.lat, lng: hostPin.lng }}
                zoom={16}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                  zoomControl: false,
                  draggable: false,
                  scrollwheel: false,
                  gestureHandling: "none",
                  clickableIcons: false,
                  disableDoubleClickZoom: true,
                }}
              />
            ) : (
              <div className="h-full w-full animate-pulse bg-gray-200 dark:bg-gray-700" />
            )}
            {isLoaded && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="flex flex-col items-center" style={{ marginTop: "-18px" }}>
                  <svg width="28" height="36" viewBox="0 0 28 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 0C6.268 0 0 6.268 0 14c0 9.625 12.542 21.188 13.083 21.708a1.312 1.312 0 001.834 0C15.458 35.188 28 23.625 28 14 28 6.268 21.732 0 14 0z" fill="#EF4444" />
                    <circle cx="14" cy="14" r="5" fill="white" />
                  </svg>
                  <div className="w-1 h-2 bg-red-500 rounded-b" />
                </div>
              </div>
            )}
          </div>
          <div className="flex items-start gap-3 px-4 py-3">
            <MapPinIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {hostPin.label || "Group spot"}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {hostPin.lat.toFixed(5)}, {hostPin.lng.toFixed(5)}
              </p>
            </div>
            <button
              type="button"
              onClick={onOpenPicker}
              className="flex-shrink-0 text-xs font-medium text-primary hover:underline"
            >
              Edit pin
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={onOpenPicker}
          className="mt-2 inline-flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <MapPinIcon className="h-4 w-4" />
          Set group spot
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// DateAdvisoryCard — one amber callout that holds every date-related heads-up:
// the attendance warnings (postpone impact, short lead time) stacked at top, and
// the "notify attendees" action in a divided footer when the event already has
// people going. Consolidating them keeps the field from stacking three separate
// amber blocks. Renders nothing when there's neither a warning nor an action.
// ---------------------------------------------------------------------------
const DateAdvisoryCard = ({ warnings = [], newDate, onNotify }) => {
  const hasWarnings = warnings.length > 0;
  if (!hasWarnings && !onNotify) return null;
  const when = toDateSafe(newDate);
  return (
    <div className="mt-2 rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2">
      <div className="flex gap-2">
        <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
        <div className="min-w-0 flex-1 space-y-1.5">
          {warnings.map((warning, i) => (
            <p key={i} className="text-xs text-amber-700 dark:text-amber-300">
              {warning}
            </p>
          ))}
          {onNotify && (
            <div
              className={`flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between ${
                hasWarnings
                  ? "border-t border-amber-200/70 dark:border-amber-800/70 pt-2"
                  : ""
              }`}
            >
              <p className="text-xs text-amber-700 dark:text-amber-300">
                People already RSVP'd
                {when ? ` for the old time` : ""} — let them know it
                {when ? ` moved to ${formatEventDate(when)}` : " changed"}.
              </p>
              <button
                type="button"
                onClick={onNotify}
                className="shrink-0 self-start sm:self-auto inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
              >
                <BellIcon className="h-4 w-4" />
                Notify attendees
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const EventDateInput = ({
  selectedDate,
  savedDate = null,
  onChange,
  isEditMode = false,
  onNotifyDateChange = null,
}) => {
  // Lead-time warning is for brand-new events only — nudge the host to leave
  // enough runway while they're first picking a date. Existing events skip it
  // (the host already committed to a date; the date-change card covers postpones).
  // getDateWarning also returns null for past/far-enough-out dates.
  const warning = isEditMode ? null : getDateWarning(selectedDate);
  // Postponing an established event more than a week later depresses turnout,
  // proportional to how far it's pushed. Only fires against the saved date, so
  // it's inherently edit-mode-only (no saved date in create).
  const pushWarning = getDatePushWarning(savedDate, selectedDate);
  const isPast =
    !isEditMode &&
    selectedDate instanceof Date &&
    !isNaN(selectedDate.getTime()) &&
    selectedDate.getTime() < Date.now();
  // Order: attendance impact first (postpone), then lead time. isPast is
  // create-only and never coexists with these, so it stays a standalone line.
  const advisoryWarnings = isPast ? [] : [pushWarning, warning].filter(Boolean);
  // When any messaging shows between the label and input, match the input's top
  // gap to the label→messaging gap (8px) so the block is evenly spaced. Bare
  // fields keep the tighter 4px default shared with the other form inputs.
  const hasMessaging =
    isPast || advisoryWarnings.length > 0 || Boolean(onNotifyDateChange);
  return (
    <div>
      <label
        htmlFor="date"
        className="block text-sm font-medium text-gray-500 dark:text-gray-400"
      >
        Event Date & Time
      </label>

      {/* All date-related messages sit between the label and the input, in one
          consolidated amber card (plus the create-only past-date line). */}
      {isPast && (
        <p className="mt-2 text-xs text-red-500 dark:text-red-400">
          Event date is in the past.
        </p>
      )}
      <DateAdvisoryCard
        warnings={advisoryWarnings}
        newDate={selectedDate}
        onNotify={onNotifyDateChange}
      />

      <input
        type="datetime-local"
        value={
          selectedDate
            ? new Date(
              selectedDate.getTime() -
              selectedDate.getTimezoneOffset() * 60000
            )
              .toISOString()
              .slice(0, -8)
            : ""
        }
        onKeyDown={(e) => {
          if (e.key === "Backspace" || e.key === "Delete") {
            e.stopPropagation();
          }
        }}
        onChange={(e) => {
          if (e.target.value) {
            onChange(new Date(e.target.value));
          }
        }}
        className={`${
          hasMessaging ? "mt-2" : "mt-1"
        } block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm sm:text-base`}
      />
    </div>
  );
};

export const StatusCheckboxes = ({
  isActive,
  isCompleted,
  onActiveChange,
  onCompletedChange,
  canToggleCompleted,
  // Co-hosts can't switch an event off (GIS-222): hiding it is cancelling it
  // without telling anyone.
  canToggleActive = true,
}) => (
  <div className="flex justify-evenly ">
    {canToggleActive && (
      <Checkbox label="Active" checked={isActive} onChange={onActiveChange} />
    )}
    {canToggleCompleted && (
      <Checkbox
        label="Completed"
        checked={isCompleted}
        onChange={onCompletedChange}
      />
    )}
  </div>
);

// `type="button"` is not optional here: these render inside the event <form>, so
// an unset type would make every menu item submit it.
const ActionMenuItem = ({ onClick, icon: Icon, children, destructive = false }) => (
  <MenuItem>
    {({ focus }) => (
      <button
        type="button"
        onClick={onClick}
        className={`flex w-full items-center px-4 py-2 text-left text-sm ${
          destructive
            ? `text-red-600 dark:text-red-400 ${focus ? "bg-red-50 dark:bg-red-900/30" : ""}`
            : focus
              ? "bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              : "text-gray-700 dark:text-gray-200"
        }`}
      >
        <Icon className="h-4 w-4 mr-2 flex-shrink-0" aria-hidden="true" />
        {children}
      </button>
    )}
  </MenuItem>
);

// Everything that isn't "save" lives behind one "..." — the same shape the event
// cards use. Keeps cancel and delete from sitting exposed beside the primary
// action, where a destructive click is one slip away. `anchor` portals the panel
// so surrounding overflow can't clip it.
// `hasGuests` false => nobody but the host is on the event, so cancelling would
// refund nothing and notify nobody. The host gets a plain "Delete event" instead
// of being walked through the cancellation wizard for a ghost. The backend
// enforces the same rule, so this only decides which affordance to show.
// `canEndEvent` false (a co-host, GIS-222) drops cancel, restore and delete,
// which stay with the host. With nothing left to offer, the menu isn't drawn.
const EventActionsMenu = ({ isCancelled, hasGuests, onDuplicate, onCancel, onUncancel, onDelete, loading, canDuplicate = true, canEndEvent = true }) => (
  <Menu as="div">
    <MenuButton
      type="button"
      title="Event actions"
      disabled={loading}
      className="inline-flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-700 p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
    >
      <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
    </MenuButton>
    <MenuItems
      anchor="bottom start"
      className="z-20 mt-2 w-52 rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-white/10 focus:outline-none"
    >
      {canDuplicate && (
        <ActionMenuItem onClick={onDuplicate} icon={DocumentDuplicateIcon}>
          Duplicate event
        </ActionMenuItem>
      )}

      {canDuplicate && canEndEvent && <div className="my-1 h-px bg-gray-100 dark:bg-gray-700" />}

      {!canEndEvent ? null : isCancelled ? (
        <>
          <ActionMenuItem onClick={onUncancel} icon={ArrowUturnLeftIcon}>
            Restore event
          </ActionMenuItem>
          <ActionMenuItem onClick={onDelete} icon={TrashIcon} destructive>
            Delete event
          </ActionMenuItem>
        </>
      ) : hasGuests ? (
        <ActionMenuItem onClick={onCancel} icon={XCircleIcon} destructive>
          Cancel event
        </ActionMenuItem>
      ) : (
        <ActionMenuItem onClick={onDelete} icon={TrashIcon} destructive>
          Delete event
        </ActionMenuItem>
      )}
    </MenuItems>
  </Menu>
);

export const FormActions = ({ isExistingEvent, isCancelled, hasGuests, onDuplicate, onCancel, onUncancel, onDelete, submitLabel, submitDisabled = false, loading, progressMessage, canDuplicate = true, canEndEvent = true }) => {
  const busyLabel = isExistingEvent ? "Updating…" : "Creating…";
  return (
    <div className="mt-6 flex items-center justify-between">
      {isExistingEvent && (canDuplicate || canEndEvent) ? (
        <EventActionsMenu
          isCancelled={isCancelled}
          hasGuests={hasGuests}
          onDuplicate={onDuplicate}
          onCancel={onCancel}
          onUncancel={onUncancel}
          onDelete={onDelete}
          loading={loading}
          canDuplicate={canDuplicate}
          canEndEvent={canEndEvent}
        />
      ) : (
        <p></p>
      )}
      <div className="flex items-center gap-x-4">
        {loading && progressMessage && (
          <span className="text-xs text-gray-500 dark:text-gray-400" aria-live="polite">
            {progressMessage}
          </span>
        )}
        <PrimaryButtonSmall
          type="submit"
          loading={loading}
          disabled={submitDisabled}
          loadingLabel={busyLabel}
        >
          {submitLabel}
        </PrimaryButtonSmall>
      </div>
    </div>
  );
};

// The event's AI hero clip: the opt-in checkbox, and under it how the clip on
// the SAVED doc is doing. It sits with the photos because the clip is made from
// the first photo.
//
// The checkbox is form state (the host owns it, it saves with the event). The
// status is not: videoStatus/videoUrl/videoError are server-owned, so they are
// deliberately absent from the form's field allowlist and would always read
// undefined there. They come from `eventData`, the live doc stream, which also
// means the status walks itself from queued to ready without a refresh.
//
// `canRegenerate` is the caller's call, not the checkbox's: asking for a new
// clip acts on the saved doc, so it stays off until the opt-in is actually saved.
export const EventVideoControl = ({
  enabled,
  onEnabledChange,
  videoStatus,
  videoUrl,
  videoError,
  onRegenerate,
  canRegenerate,
}) => {
  const pending = videoStatus === "queued" || videoStatus === "generating";
  const failed = videoStatus === "failed";
  const ready = !pending && !failed && Boolean(videoUrl);

  return (
    <div>
      <Checkbox
        label="Make a short video from the first photo"
        checked={!!enabled}
        onChange={onEnabledChange}
        labelClassName="text-sm font-medium text-gray-500 dark:text-gray-400"
      />
      <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
        The video is made when you save, and only for an active event that hasn't happened yet.
      </p>

      {(pending || failed || ready) && (
        <div
          className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
          aria-live="polite"
        >
          {pending && (
            <span className="text-gray-500 dark:text-gray-400">
              Making the video now, this takes a minute or two.
            </span>
          )}
          {failed && (
            <span className="text-red-600 dark:text-red-400" title={videoError || undefined}>
              Didn't work{videoError ? `: ${videoError}` : ""}
            </span>
          )}
          {ready && (
            <a
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              Preview
              <ArrowTopRightOnSquareIcon className="h-3 w-3" aria-hidden="true" />
            </a>
          )}
          {/* An unticked box hides the clip from guests, it never deletes it. Say
              so, both to warn before a save hides an existing video and so nobody
              thinks the work was thrown away. */}
          {ready && !enabled && (
            <span className="text-gray-500 dark:text-gray-400">
              Guests see it only while this box is ticked. The file is kept either way.
            </span>
          )}
          {(ready || failed) && canRegenerate && (
            <button
              type="button"
              onClick={onRegenerate}
              className="font-medium text-primary hover:underline"
            >
              {failed ? "Try again" : "Regenerate"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Per-event mixer check-in switch. On (the default, and what every event written
// before the field existed means) is the mixer: guests get the check-in reminder,
// check in on the app and the Queue pairs them for rounds. Off is a plain meetup:
// no reminder, no rounds, the server marks the event complete on its own (2 hours
// after start unless a duration is given) and post-event follow-ups go to
// everyone who RSVP'd.
export const CheckInControl = ({
  requireCheckIn,
  onRequireCheckInChange,
  duration,
  onDurationChange,
}) => {
  const enabled = requireCheckIn !== false;
  return (
    <div>
      <Checkbox
        label="Mixer check-in"
        checked={enabled}
        onChange={onRequireCheckInChange}
        labelClassName="text-sm font-medium text-gray-500 dark:text-gray-400"
      />
      <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
        {enabled
          ? "Guests get a check-in reminder and the app pairs them for rounds from the Queue."
          : "No check-in reminders and no rounds. The event completes on its own after it ends. Follow-ups go to everyone who RSVP'd."}
      </p>
      {!enabled && (
        <label className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          Duration (hours)
          <input
            type="number"
            min="0.5"
            step="0.5"
            value={duration ?? ""}
            placeholder="2"
            onChange={(e) =>
              onDurationChange(e.target.value === "" ? null : Number(e.target.value))
            }
            className="w-24 rounded-md border-0 py-1 px-2 text-gray-900 dark:text-gray-100 dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-primary text-sm"
          />
          <span className="text-xs text-gray-400 dark:text-gray-500">
            Leave blank for 2 hours.
          </span>
        </label>
      )}
    </div>
  );
};

export const EventUrlInput = ({ eventId }) => {
  const eventUrl = eventId
    ? eventDetailsUrl(eventId)
    : "";

  if (!eventId) return null;

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(eventUrl);
    showToast("URL copied to clipboard!", { type: "success" });
  };

  const handleOpenInNewTab = () => {
    window.open(eventUrl, "_blank");
  };

  return (
    <div>
      <label
        htmlFor="eventUrl"
        className="block text-sm font-medium text-gray-500 dark:text-gray-400"
      >
        Event URL
      </label>
      <div className="relative mt-1">
        <input
          type="text"
          id="eventUrl"
          value={eventUrl}
          readOnly
          onClick={(e) => e.target.select()}
          className="text-gray-700 dark:text-gray-200 text-xs block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500  sm:text-sm bg-gray-50 dark:bg-gray-800 pr-[65px]"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 space-x-2">
          <button
            type="button"
            onClick={handleCopyToClipboard}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <DocumentDuplicateIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={handleOpenInNewTab}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <ArrowTopRightOnSquareIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

